import { errorMessage } from '../services/apiError';
import { redirectAfterAuth, savePostAuthRedirect } from '../services/postAuthRedirect';

const getErrorMessage = (err: unknown): string => errorMessage(
  err,
  'We couldn’t complete authentication. Check your details and try again.',
);

const extractLoginError = (err: unknown): string => getErrorMessage(err);

const isRegistration = (displayName?: string): boolean => !!displayName;

const executeAuth = async (emailVal: string, password: string, displayName?: string, country?: string, stateCode?: string) => {
  const { authService } = await import('../services/authService');

  if (isRegistration(displayName) && country) {
    return authService.emailRegister(emailVal, password, displayName!, country, stateCode);
  }

  return authService.emailLogin(emailVal, password);
};

const completeLogin = (onClose: () => void) => {
  onClose();
  redirectAfterAuth();
};

const performEmailAuth = async (
  emailVal: string,
  password: string,
  onClose: () => void,
  setError: (error: string | undefined) => void,
  displayName?: string,
  country?: string,
  stateCode?: string,
  onDisposableEmail?: () => void
) => {
  const signingUp = isRegistration(displayName);

  if (!signingUp) savePostAuthRedirect();

  const result = await executeAuth(emailVal, password, displayName, country, stateCode);

  const { trackLogin } = await import('../utils/analytics');

  // Registration: show verification message instead of reloading
  if (signingUp) {
    setError('Registration successful! Please check your email to verify your account.');

    // Tempmail signups get no free credits — let the caller show a notice
    if ('disposable_email' in result && result.disposable_email) {
      onDisposableEmail?.();
    }

    return;
  }

  trackLogin('email');
  completeLogin(onClose);
};

const handleLoginError = (err: unknown, setError: (error: string | undefined) => void) => {
  setError(extractLoginError(err));
};

const initLoginState = (
  setIsLoading: (loading: boolean) => void,
  setError: (error: string | undefined) => void
): void => {
  setIsLoading(true);
  setError(undefined);
};

export const createEmailAuthHandler = (
  setIsLoading: (loading: boolean) => void,
  setError: (error: string | undefined) => void,
  onClose: () => void,
  onDisposableEmail?: () => void
) => async (emailVal: string, password?: string, displayName?: string, country?: string, stateCode?: string) => {
  initLoginState(setIsLoading, setError);

  try {
    await performEmailAuth(emailVal, password || '', onClose, setError, displayName, country, stateCode, onDisposableEmail);
  } catch (err: unknown) {
    handleLoginError(err, setError);
  } finally {
    setIsLoading(false);
  }
};
